import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Inject, Component, OnInit } from "@angular/core";
import { AreaSummary } from "../models/area.summary.interface";
import { NgForm } from "@angular/forms";
import { QuoteService } from "../services/quote.service";
import { finalize } from "rxjs/operators";
import { QuoteBase } from "../../shared/models/quote.base.interface";
import { PaymentTerm } from "../../shared/models/payment.term.interface";
import { PaymentType } from "../../shared/models/payment.type.interface";
import { DeviceDetectorService } from "ngx-device-detector";
import { QuoteArea } from "../../shared/models/quote.area.interface";
import { AbstractBiditComponent } from "../../shared/controls/abstract.bidit.component";
import { QuoteAcknowledgementAreaItem } from "../../shared/models/quote acknowledgement.area.item.interface";
import { CalculateService } from "../../shared/services/calculate.service";
import { DealerService } from "../../dealer/services/dealer.service";
import { QuoteAreaOptionGroup } from "../../shared/models/quoteAreaOptionGroup.interface";
import { RolePermission } from "../../shared/models/role.permission.enum";

export interface ConvertDialogData {
  quote: QuoteBase;
}

@Component({
  selector: 'convert.dialog',
  templateUrl: 'convert.dialog.html',
  styleUrls: ['./convert.dialog.scss']
})
export class ConvertDialog extends AbstractBiditComponent implements OnInit {

  paymentTypes: PaymentType[];
  paymentTerms: PaymentTerm[];

  area_IsAccepted305: any;

  upgradeAreaSummaries: AreaSummary[] = new Array();

  quote: QuoteBase = {} as QuoteBase;
  acknowledgedQuoteAreaItemsSelected: QuoteAcknowledgementAreaItem[];

  showMarginEdit: boolean;
  showBidAmountEdit: boolean;
  quoteTotalBidAmount: number;
  totalLaborCost: number;
  totalMaterialCost: number;
  totalOtherCost: number;
  totalMarginDollars: number;

  materialTaxTotal: number;
  laborTaxTotal: number;
  otherTaxTotal: number;
  netTaxTotal: number;
  dealerRoundingType: number;
  showMargins: boolean = false;


  constructor(
    private quoteService: QuoteService,
    private dealerService: DealerService,
    private calculateService: CalculateService,
    deviceDetectorService: DeviceDetectorService,
    public dialogRef: MatDialogRef<ConvertDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ConvertDialogData) {
    super(deviceDetectorService);
  }

  ngOnInit() {

    this.getDealerRoundingType();

    this.quoteService.getPaymentTerms()
      .subscribe(result => {
        this.paymentTerms = result;
      });

    this.quoteService.getPaymentTypes()
      .subscribe(result => {
        this.paymentTypes = result;
      });

    this.showMargins = QuoteService.hasPermission(RolePermission.ViewEditMargins);
  }

  getQuote(): void {
    this.quoteService.getQuote(this.data.quote.quote_ID)
      .subscribe(
        {
          next: (result) => {
            this.upgradeAreaSummaries = this.calculateUpgradeAreas(result.areaSummaries, this.acknowledgedQuoteAreaItemsSelected);
          },
          complete: () => { this.calculateConversion(); }
        });
  }

  getAcknowledgedAreaItems(quoteID: number): void {
    this.quoteService.quoteAcknowledgedItems(quoteID)
      .subscribe({
        next: (result) => {
          this.acknowledgedQuoteAreaItemsSelected = result;
        },
        complete: () => { this.getQuote(); }
      });
  }

  getDealerRoundingType(): void {
    this.dealerService.getDealerDetails()
      .subscribe({
        next: (result) => {
          this.dealerRoundingType = result.dealer_Rounding;
        },
        complete: () => { this.getAcknowledgedAreaItems(this.data.quote.quote_ID); },
        error: (error: string) => { this.errors = error; }
      });
  }

  calculateUpgradeAreas(areaSummaries: AreaSummary[], acknowledgeSelections: QuoteAcknowledgementAreaItem[]): AreaSummary[] {
    this.quote = (<any>Object).assign({}, this.data.quote) as QuoteBase;
    this.quote.optionCost = 0;
    this.quote.totalCost = 0;

    let quoteMargin = (100 - this.data.quote.quote_GrossMargin) / 100;

    areaSummaries.forEach(a => {
      a.quoteAreas.forEach(qa => {
        if (a.area_IsOption) {
          qa.area_RadioValue = this.getAcknowledgedAreaItem(acknowledgeSelections, qa, a.area_IsOption);
        }
        else {
          qa.area_RadioValue = this.getAcknowledgedAreaItem(acknowledgeSelections, qa, a.area_IsOption);
          this.quote.totalCost += qa.quoteItem.area_TotalCost;
        }

        qa.quoteUpgrades.forEach(u => {
          u.upgradeBid = (u.area_TotalCost / quoteMargin) - (qa.quoteItem.area_TotalCost / quoteMargin);
          u.upgradeDelta = (u.area_TotalCost - qa.quoteItem.area_TotalCost);
        });
      });

      if (a.quoteAreaOptionGroups !== null) {
        a.quoteAreaOptionGroups.forEach(og => {
          var acceptedCount = 0;
          og.quoteAreaItems.forEach(qai => {
            var itemForArea = this.acknowledgedQuoteAreaItemsSelected.find(x => x.quoteAreaItem_ID == qai.quoteAreaItem_ID);
            qai.area_IsAccepted = itemForArea?.quoteAreaItem_ID > 0 ? true : false;
            acceptedCount += itemForArea?.quoteAreaItem_ID > 0 ? 1 : 0;
          });

          if (og.optionGroup !== null) {
            og.group_radioValue = acceptedCount === og.quoteAreaItems.length ? 1 : 0
            og.group_isAccepted = acceptedCount === og.quoteAreaItems.length;
          }
        })
      }
    });

    return areaSummaries;
  }

  setOptionGroupSelected(selectedOptionGroup: number, selected: number) {
    this.upgradeAreaSummaries.filter(qas => qas.area_IsOption).forEach(a => {
      let optionGroup = a.quoteAreaOptionGroups.find(qag => qag.optionGroup === selectedOptionGroup) as QuoteAreaOptionGroup

      optionGroup.group_isAccepted = selected != 0;

      optionGroup.quoteAreaItems.forEach(qai => {
        qai.area_IsAccepted = selected != 0;
      });
    });

    this.calculateConversion();
  }

  setOptionSelected(quoteAreaItemId: number, isSelected: false) {
    this.upgradeAreaSummaries.filter(qas => qas.area_IsOption).forEach(a => {
      let optionGroup = a.quoteAreaOptionGroups.find(qag => qag.optionGroup === null) as QuoteAreaOptionGroup

      optionGroup.quoteAreaItems.find(qai => qai.quoteAreaItem_ID === quoteAreaItemId).area_IsAccepted = isSelected;
    });

    this.calculateConversion();
  }

  calculateOptionsTotal() {
    let optionGroupTotal = 0;

    this.upgradeAreaSummaries.filter(qas => qas.area_IsOption).forEach(a => {

      a.quoteAreaOptionGroups.forEach(og => {
        if (og.group_isAccepted) {
          og.quoteAreaItems.forEach(qai => {
            optionGroupTotal += qai.optionBid;
          })
        }
      });

      let baseOptionGroup = a.quoteAreaOptionGroups.find(og => og.optionGroup == null);
      baseOptionGroup?.quoteAreaItems.filter(qai => qai.area_IsAccepted).forEach(qai => {
        optionGroupTotal += qai.optionBid;
      })
    });

    this.quote.optionCost += optionGroupTotal;
  }

  getAcknowledgedAreaItem(acknowledgedItems: QuoteAcknowledgementAreaItem[], quoteArea: QuoteArea, isOption: boolean): number {
    var selectedAreaItem = quoteArea.quoteItem.quoteAreaItem_ID;
    if (acknowledgedItems.length > 0) {
      var itemForArea = this.acknowledgedQuoteAreaItemsSelected.find(x => x.quoteArea_ID == quoteArea.quoteArea_ID);
    }

    if (isOption) {
      var selectedItem = itemForArea?.quoteAreaItem_ID ?? 0;
    }
    else {
      var selectedItem = itemForArea?.quoteAreaItem_ID ?? selectedAreaItem;
    }
    return selectedItem;
  }

  get quoteMarginAmount(): number {
    return this.quote.quote_BidAmount - this.quote.totalCost;
  }

  editBid() {
    this.showBidAmountEdit = true;
  }

  editMargin() {
    this.showMarginEdit = true;
  }

  calculateProjectCost() {
    this.quote.totalCost = 0;
    this.quote.optionCost = 0;
    this.totalLaborCost = 0;
    this.totalMaterialCost = 0;
    this.totalOtherCost = 0;

    this.upgradeAreaSummaries.forEach(a => {
      a.quoteAreas.forEach(qa => {
        let qai = qa.quoteAreaItems.find(qai => qai.quoteAreaItem_ID == qa.area_RadioValue);
        if (qai != null) {
          this.quote.totalCost += (qa.area.area_IsOption ? 0 : qai.area_TotalCost);
          this.totalLaborCost += qai.area_TotalLaborCost;
          this.totalMaterialCost += qai.area_MaterialCost;
          this.totalOtherCost += qai.area_OtherCosts;
        }
      });
    });

    this.calculateOptionsTotal();
    this.calcTaxTotals();
    this.totalMarginDollars = this.quoteMarginAmount;
  }

  calcTaxTotals() {
    this.quote.materialTax_Rate ?
      (this.materialTaxTotal = (this.quote.materialTax_Rate * this.totalMaterialCost) / 100) :
      this.materialTaxTotal = this.quote.materialTax_Total;

    this.quote.laborTax_Rate ?
      (this.laborTaxTotal = (this.quote.laborTax_Rate * this.totalLaborCost) / 100) :
      this.laborTaxTotal = this.quote.laborTax_Total;

    this.quote.otherTax_Rate ?
      (this.otherTaxTotal = (this.quote.otherTax_Rate * this.totalOtherCost) / 100) :
      this.otherTaxTotal = this.quote.otherTax_Total;

    this.netTaxTotal = this.materialTaxTotal + this.laborTaxTotal + this.otherTaxTotal;
  }

  calculateConversion() {
    this.calculateProjectCost();
    let marginRatio = (100 - (this.quote.quote_GrossMargin ?? 0)) / 100;
    let productAmount = this.quote.totalCost / marginRatio;
    this.quote.quote_BidAmount = Math.round((productAmount + this.quote.optionCost) * 100) / 100;

    if (this.quote.quote_BidAmount === Infinity || this.quote.quote_BidAmount === -Infinity) {
      this.quote.quote_BidAmount = 0;
    }

    if (this.quote.quote_BidAmount === 0) {
      console.log(`Before Round Quote ${this.quote.quote_ID} Gross Margin ${this.quote.quote_GrossMargin} Total Cost ${this.quote.totalCost} Margin Ratio ${marginRatio} Product Amount ${productAmount} Bid Amount ${this.quote.quote_BidAmount} Dealer Rounding ${this.dealerRoundingType}`)
    }

    this.quote.quote_BidAmount = this.calculateService.GetRoundedAmount(this.dealerRoundingType, this.quote.quote_BidAmount);

    if (this.quote.quote_BidAmount === 0) {
      console.log(`After Round Quote ${this.quote.quote_ID} Gross Margin ${this.quote.quote_GrossMargin} Total Cost ${this.quote.totalCost} Margin Ratio ${marginRatio} Product Amount ${productAmount} Bid Amount ${this.quote.quote_BidAmount} Dealer Rounding ${this.dealerRoundingType}`)
    }

    this.quoteTotalBidAmount = this.quote.quote_BidAmount + this.netTaxTotal;
  }

  updateConvertMargin() {
    this.showMarginEdit = false;

    this.quote.quote_GrossMargin = Math.round(this.quote.quote_GrossMargin * 10000) / 10000;
    this.calculateConversion();
  }

  updateConvertBid() {
    this.showBidAmountEdit = false;

    this.calculateProjectCost();

    this.quote.quote_BidAmount = this.calculateService.GetRoundedAmount(this.dealerRoundingType, this.quote.quote_BidAmount);

    let marginRation = (this.quote.totalCost / this.quote.quote_BidAmount);
    this.quote.quote_GrossMargin = (100 - (100 * marginRation));

    this.quote.quote_GrossMargin = Math.round(this.quote.quote_GrossMargin * 10000) / 10000;
    if (this.quote.quote_GrossMargin === -Infinity || this.quote.quote_GrossMargin === Infinity) {
      this.quote.quote_GrossMargin = 0;
    }
  }

  convertQuote(form: NgForm) {
    this.errors = '';
    if (this.quote.quote_BidAmount === 0) {
      this.showDialog('Bid Amount Calculated is 0 - Continue? ', 'Bid Amount Notification', 'Yes', 'No')
        .subscribe((data) => {
          if (!data) {
            return;
          }
          else {
            this.continueConvert(form);
          }
        });
    }
    else {
      this.continueConvert(form);
    }
  }

  continueConvert(form: NgForm) {
    if (form.valid) {
      let acceptedAreas: number[] = [];
      this.upgradeAreaSummaries.forEach(a => {
        var selections = a.quoteAreas.map((qa: QuoteArea): number => {
          return qa.area_RadioValue;
        });
        acceptedAreas = acceptedAreas.concat(selections);

        var optionSelections = [];
        this.upgradeAreaSummaries.filter(qas => qas.area_IsOption).forEach(a => {
          a.quoteAreaOptionGroups.forEach(og => {
            og.quoteAreaItems.filter(qai => qai.area_IsAccepted).forEach(
              i => { optionSelections.push(i.quoteAreaItem_ID) });
          });
        });

        acceptedAreas = acceptedAreas.concat(optionSelections);
      });

      this.quoteService.convertQuote(this.data.quote.quote_ID,
        this.quote.quote_PaymentType_ID,
        this.quote.quote_PaymentTerm_ID,
        this.quote.quote_BidAmount,
        acceptedAreas)
        .pipe(finalize(() => this.isRequesting = false))
        .subscribe(
          {
            next: (result) => {
              if (result) {
                this.dialogRef.close(this.data);
              }
            },
            error: (error: string) => this.errors = error
          });
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}
